import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useLang } from "../hooks/useLang";
import { useTheme } from "../hooks/useTheme";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBars, FaTimes, FaGlobe, FaSun, FaMoon,
  FaBuilding, FaGraduationCap, FaSchool, FaChevronDown,
} from "react-icons/fa";
import logo from "@assets/logoMindbridge_1776100737645.png";

const GOLD = "#C9A84C";
const DARK_BG = "#2A2E3A";

const LANGS: { code: "fr" | "ar" | "en"; label: string }[] = [
  { code: "fr", label: "FR" },
  { code: "ar", label: "AR" },
  { code: "en", label: "EN" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { currentLang, setLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [offresOpen, setOffresOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [location, navigate] = useLocation();
  const offresRef = useRef<HTMLDivElement>(null);
  const isRTL = currentLang === "ar";

  const NAVBAR_H = 88;

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_H;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleHashClick = (e: React.MouseEvent, href: string) => {
    if (!href.startsWith("/#")) return;
    e.preventDefault();
    const sectionId = href.slice(2);
    if (location === "/") {
      scrollToSection(sectionId);
    } else {
      sessionStorage.setItem("pendingScroll", sectionId);
      navigate("/");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = ["apropos", "offres", "pourquoi", "services", "processus", "temoignages", "blog", "contact", "faq"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.3, rootMargin: "-80px 0px 0px 0px" }
    );
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [location]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (offresRef.current && !offresRef.current.contains(e.target as Node)) {
        setOffresOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setIsOpen(false); setOffresOpen(false); }, [location]);

  const cycleLang = () => {
    const idx = LANGS.findIndex((l) => l.code === currentLang);
    const next = LANGS[(idx + 1) % LANGS.length];
    setLang(next.code);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next.code);
    window.history.replaceState({}, "", url.toString());
  };

  const isActive = (hash: string) => {
    if (hash === "") return location === "/" && activeSection === "";
    return activeSection === hash;
  };

  const navLinks = [
    { href: "/#accueil", label: t("navbar.accueil"), id: "" },
    { href: "/#apropos", label: t("navbar.apropos"), id: "apropos" },
    { href: "/vision", label: t("navbar.vision"), id: "" },
    { href: "/blog", label: t("navbar.blog"), id: "blog" },
    { href: "/contact", label: t("navbar.contact"), id: "contact" },
    { href: "/faq", label: t("navbar.faq"), id: "faq" },
  ];

  const offresLinks = [
    { href: "/entreprise", label: t("navbar.entreprise"), icon: FaBuilding },
    { href: "/formations", label: t("navbar.formations"), icon: FaGraduationCap },
    { href: "/ecole", label: t("navbar.ecole"), icon: FaSchool },
  ];

  const isDark = theme === "dark";

  const glassStyle: React.CSSProperties = scrolled
    ? {
        background: isDark ? "rgba(42,46,58,0.90)" : "rgba(245,247,250,0.92)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      }
    : isDark
    ? { background: "transparent" }
    : {
        background: "rgba(245,247,250,0.80)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(0,0,0,0.04)",
      };

  const linkColor = (id: string, href: string) => {
    const onPage = location !== "/" && href.startsWith(location) && href === location;
    const inSection = isActive(id);
    return onPage || inSection ? GOLD : isDark ? "#d1d5db" : "#374151";
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={glassStyle}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <img src={logo} alt="MindBridge" className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-110" />
            <span className={`font-bold text-xl tracking-wide ${isDark ? "text-white" : "text-gray-900"}`}>
              Mind<span style={{ color: GOLD }}>Bridge</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="relative px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 hover:text-[#C9A84C]"
                style={{ color: linkColor(link.id, link.href) }}
                onClick={(e) => handleHashClick(e, link.href)}
              >
                {link.label}
                {(isActive(link.id) && link.id) && (
                  <motion.span layoutId="nav-underline" className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{ background: GOLD }} />
                )}
              </Link>
            ))}

            {/* Nos Offres dropdown */}
            <div className="relative" ref={offresRef}>
              <button
                onClick={() => setOffresOpen(!offresOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-[#C9A84C]"
                style={{ color: isDark ? "#d1d5db" : "#374151" }}
              >
                {t("navbar.offres")}
                <motion.span animate={{ rotate: offresOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <FaChevronDown size={10} />
                </motion.span>
              </button>
              <AnimatePresence>
                {offresOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.18 }}
                    className="absolute top-full mt-2 rounded-xl shadow-2xl overflow-hidden min-w-[200px]"
                    style={{
                      background: isDark ? DARK_BG : "#fff",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                      [isRTL ? "right" : "left"]: 0,
                    }}
                  >
                    {offresLinks.map(({ href, label, icon: Icon }) => (
                      <Link
                        key={href}
                        href={href}
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        onClick={() => setOffresOpen(false)}
                      >
                        <Icon size={14} style={{ color: GOLD }} />
                        {label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right controls */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Lang toggle */}
            <button
              onClick={cycleLang}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors hover:bg-[#C9A84C]/10 hover:text-[#C9A84C]"
              style={{ color: isDark ? "#d1d5db" : "#374151" }}
              title="Switch language"
              aria-label={`Changer la langue (actuelle: ${currentLang.toUpperCase()})`}
            >
              <FaGlobe size={13} />
              {currentLang.toUpperCase()}
            </button>

            {/* Dark/light toggle */}
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-[#C9A84C]/10"
              style={{ color: isDark ? "#d1d5db" : "#374151" }}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
            >
              {isDark ? <FaSun size={15} /> : <FaMoon size={15} />}
            </button>

            {/* CTA */}
            <Link
              href="/contact"
              className="px-5 py-2.5 text-sm font-bold rounded-xl transition-all duration-200 hover:scale-105 hover:shadow-lg"
              style={{ background: GOLD, color: DARK_BG, boxShadow: `0 4px 16px ${GOLD}40` }}
            >
              {t("navbar.cta_app")}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 transition-colors"
            style={{ color: isDark ? "#d1d5db" : "#374151" }}
            aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile full-screen overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed inset-0 top-20 z-40 flex flex-col"
            style={{
              background: isDark ? "rgba(15,17,23,0.98)" : "rgba(245,247,250,0.98)",
              backdropFilter: "blur(16px)",
            }}
          >
            <div className="px-6 py-8 space-y-2 flex-1 overflow-y-auto">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07 }}
                >
                  <Link
                    href={link.href}
                    className="block px-4 py-3.5 rounded-xl text-base font-medium transition-colors"
                    style={{
                      color: isActive(link.id) ? GOLD : isDark ? "#d1d5db" : "#374151",
                      background: isActive(link.id) ? `${GOLD}15` : "transparent",
                    }}
                    onClick={(e) => { setIsOpen(false); handleHashClick(e, link.href); }}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-2">
                <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest`} style={{ color: GOLD }}>
                  {t("navbar.offres")}
                </div>
                {offresLinks.map(({ href, label, icon: Icon }, i) => (
                  <motion.div key={href} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 + i * 0.07 }}>
                    <Link
                      href={href}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors"
                      style={{ color: isDark ? "#9ca3af" : "#6b7280" }}
                      onClick={() => setIsOpen(false)}
                    >
                      <Icon size={14} style={{ color: GOLD }} />
                      {label}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Mobile bottom controls */}
            <div className="px-6 py-6 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={cycleLang}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                  style={{ color: isDark ? "#d1d5db" : "#374151", borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)" }}
                  aria-label={`Changer la langue (actuelle: ${currentLang.toUpperCase()})`}
                >
                  <FaGlobe size={13} />
                  {currentLang.toUpperCase()}
                </button>
                <button
                  onClick={toggleTheme}
                  className="w-11 h-11 rounded-xl flex items-center justify-center border transition-colors"
                  style={{ color: isDark ? "#d1d5db" : "#374151", borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)" }}
                  aria-label={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
                >
                  {isDark ? <FaSun size={16} /> : <FaMoon size={16} />}
                </button>
              </div>
              <Link
                href="/contact"
                className="block w-full text-center py-3.5 rounded-xl font-bold text-base transition-all"
                style={{ background: GOLD, color: DARK_BG }}
                onClick={() => setIsOpen(false)}
              >
                {t("navbar.cta_app")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
